import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCart, CartItem, Pago } from "../store/cart";
import { useAuth } from "../store/auth";
import { useSearch } from "../store/search";
import { api } from "../lib/api";
import { printTicket } from "../lib/printer";
import { query as sqliteQuery } from "../lib/offline-db";
import PagoPanel, { ConfirmExtras } from "../components/PagoPanel";
import BrandStrip from "../components/BrandStrip";

interface Producto {
  id: number;
  sku: string;
  barcode: string | null;
  nombre: string;
  precio: number;
  stock?: number;
  categoria?: string | null;
  pesable?: boolean;
}

interface CacheRow {
  id: number;
  sku: string;
  barcode: string | null;
  nombre: string;
  precio: number;
  stock: number;
  json: string;
}

const CATEGORIAS = [
  "Todos",
  "Bebidas",
  "Almacén",
  "Golosinas",
  "Cigarrillos",
  "Limpieza",
];

const EMOJI_BY_CAT: Record<string, string> = {
  Bebidas: "🥤",
  Almacén: "🍞",
  Golosinas: "🍫",
  Cigarrillos: "🚬",
  Limpieza: "🧴",
};

// Paleta del diseño — tokens --tile-* en index.css
interface TilePalette {
  bg: string;
  ink: string;
}

const TILE_PALETTES: TilePalette[] = [
  { bg: "var(--tile-mint)", ink: "var(--tile-mint-ink)" },
  { bg: "var(--tile-peach)", ink: "var(--tile-peach-ink)" },
  { bg: "var(--tile-lavender)", ink: "var(--tile-lavender-ink)" },
  { bg: "var(--tile-yellow)", ink: "var(--tile-yellow-ink)" },
  { bg: "var(--tile-pink)", ink: "var(--tile-pink-ink)" },
  { bg: "var(--tile-blue)", ink: "var(--tile-blue-ink)" },
];

const TILE_BY_CAT: Record<string, TilePalette> = {
  Bebidas: TILE_PALETTES[5], // blue
  Almacén: TILE_PALETTES[3], // yellow
  Golosinas: TILE_PALETTES[4], // pink
  Cigarrillos: TILE_PALETTES[1], // peach
  Limpieza: TILE_PALETTES[0], // mint
};

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

function initialsFor(nombre: string): string {
  const words = nombre.replace(/\s+/g, " ").trim().split(" ");
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return nombre.slice(0, 2).toUpperCase();
}

function emojiFor(p: Producto): string | null {
  if (p.categoria && EMOJI_BY_CAT[p.categoria]) return EMOJI_BY_CAT[p.categoria];
  const n = p.nombre.toLowerCase();
  if (n.includes("coca") || n.includes("gaseosa")) return "🥤";
  if (n.includes("agua")) return "💧";
  if (n.includes("cerveza") || n.includes("heineken") || n.includes("quilmes")) return "🍺";
  if (n.includes("yerba") || n.includes("mate")) return "🧉";
  if (n.includes("pan") || n.includes("galletit") || n.includes("chalitas")) return "🍞";
  if (n.includes("chocolate") || n.includes("milka") || n.includes("kukina")) return "🍫";
  if (n.includes("leche")) return "🥛";
  if (n.includes("cigarr") || n.includes("marlboro") || n.includes("l&m")) return "🚬";
  if (n.includes("lavandina") || n.includes("limpia") || n.includes("detergente")) return "🧴";
  if (n.includes("vino") || n.includes("whisky") || n.includes("ron") || n.includes("fernet")) return "🍷";
  if (n.includes("tomate") || n.includes("verdura") || n.includes("lechuga")) return "🥬";
  if (n.includes("caramelo") || n.includes("gomita") || n.includes("chupetin")) return "🍬";
  if (n.includes("jugo") || n.includes("juguito")) return "🧃";
  if (n.includes("queso")) return "🧀";
  if (n.includes("perfume") || n.includes("desodorante")) return "🧴";
  if (n.includes("pila") || n.includes("bateria")) return "🔋";
  if (n.includes("auto") || n.includes("juguete") || n.includes("muneca") || n.includes("muñeca")) return "🧸";
  return null; // fallback a iniciales
}

function paletteFor(p: Producto): TilePalette {
  if (p.categoria && TILE_BY_CAT[p.categoria]) return TILE_BY_CAT[p.categoria];
  return TILE_PALETTES[hashStr(p.sku || p.nombre) % TILE_PALETTES.length];
}

function tileBackground(pal: TilePalette): string {
  return `linear-gradient(135deg, ${pal.bg} 0%, color-mix(in oklch, ${pal.bg} 70%, white) 100%)`;
}

function fmt(n: number | string | null | undefined): string {
  const num = typeof n === "number" ? n : Number(n ?? 0);
  if (!Number.isFinite(num)) return "$0";
  return "$" + num.toLocaleString("es-AR", { maximumFractionDigits: 2 });
}

interface ApiProducto {
  id: number;
  sku: string;
  barcode: string | null;
  nombre: string;
  categoria: string | null;
  unidad?: string;
  precio_venta: string | number;
  stock?: string | number;
}

// Unidades que implican producto pesable (precio por kg, cantidad decimal).
const PESABLE_UNITS = new Set(["KG", "GR", "LT", "ML"]);

function isPesable(unidad?: string | null): boolean {
  return !!unidad && PESABLE_UNITS.has(unidad.toUpperCase());
}

function mapApiProducto(p: ApiProducto): Producto {
  return {
    id: p.id,
    sku: p.sku,
    barcode: p.barcode,
    nombre: p.nombre || p.sku,
    precio: Number(p.precio_venta ?? 0),
    stock: p.stock != null ? Number(p.stock) : undefined,
    categoria: p.categoria,
    pesable: isPesable(p.unidad),
  };
}

async function fetchProducts(q?: string): Promise<Producto[]> {
  const search = (q || "").trim();
  try {
    const params: Record<string, string | number> = { limit: search ? 50 : 200 };
    if (search) params.q = search;
    const { data } = await api.get<ApiProducto[]>("/api/products", { params });
    return data.map(mapApiProducto);
  } catch (e) {
    try {
      // Offline fallback: cache local + filter client-side
      const sql = search
        ? "SELECT id, sku, barcode, nombre, precio, stock, json FROM catalogo_cache WHERE LOWER(nombre) LIKE ? OR LOWER(sku) LIKE ? OR LOWER(barcode) LIKE ? LIMIT 50"
        : "SELECT id, sku, barcode, nombre, precio, stock, json FROM catalogo_cache LIMIT 200";
      const args = search ? [`%${search.toLowerCase()}%`, `%${search.toLowerCase()}%`, `%${search.toLowerCase()}%`] : [];
      const rows = await sqliteQuery<CacheRow>(sql, args);
      return rows.map((r) => {
        let extra: Partial<Producto> = {};
        try {
          extra = JSON.parse(r.json) as Partial<Producto>;
        } catch {
          /* ignore */
        }
        return {
          id: r.id,
          sku: r.sku,
          barcode: r.barcode,
          nombre: r.nombre,
          precio: r.precio,
          stock: r.stock,
          categoria: extra.categoria ?? null,
          pesable: extra.pesable,
        };
      });
    } catch {
      throw e;
    }
  }
}

/** Debounce hook simple para no llamar al backend en cada tecla. */
function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export default function Venta() {
  const cart = useCart();
  const user = useAuth((s) => s.user);
  const globalSearch = useSearch((s) => s.query);
  const [categoria, setCategoria] = useState<string>("Todos");
  const [filtro, setFiltro] = useState("");
  const [showPago, setShowPago] = useState(false);
  const [showCliente, setShowCliente] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pesableModal, setPesableModal] = useState<Producto | null>(null);
  const localFilterRef = useRef<HTMLInputElement>(null);

  // Combinacion de busqueda global (F1 topbar) + filtro local (F3) para el fetch.
  // El backend filtra entre los 4469 productos; client-side solo refina por categoria.
  const combinedSearch = (globalSearch + " " + filtro).trim();
  const debouncedSearch = useDebounced(combinedSearch, 300);

  const { data: productos = [], isLoading } = useQuery({
    queryKey: ["productos", debouncedSearch],
    queryFn: () => fetchProducts(debouncedSearch),
    staleTime: 30_000,
  });

  // El backend ya filtra por texto via ?q=. Solo refinamos por categoria aca.
  const visibles = useMemo(() => {
    if (categoria === "Todos") return productos;
    return productos.filter((p) => (p.categoria ?? "") === categoria);
  }, [productos, categoria]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowCliente(true);
      } else if (e.key === "F4") {
        e.preventDefault();
        if (cart.items.length > 0) setShowPago(true);
      } else if (e.key === "F3") {
        e.preventDefault();
        localFilterRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cart.items.length]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const addProducto = (p: Producto) => {
    if (p.pesable) {
      // Pesable: abre modal pidiendo cantidad (kg con decimales)
      setPesableModal(p);
      return;
    }
    const it: CartItem = {
      producto_id: p.id,
      sku: p.sku,
      nombre: p.nombre,
      cantidad: 1,
      precio_unit: p.precio,
      pesable: false,
    };
    cart.addItem(it);
  };

  const addPesable = (p: Producto, cantidad: number) => {
    if (!Number.isFinite(cantidad) || cantidad <= 0) return;
    cart.addItem({
      producto_id: p.id,
      sku: p.sku,
      nombre: p.nombre,
      cantidad,
      precio_unit: p.precio,
      pesable: true,
    });
    setPesableModal(null);
  };

  const cerrarVenta = async (pagos: Pago[], extras: ConfirmExtras) => {
    const total = cart.total();
    const pagado = pagos.reduce((a, p) => a + p.monto, 0);
    const local_uuid = crypto.randomUUID();
    const payload = {
      local_uuid,
      cliente_id: cart.cliente?.id ?? null,
      descuento: cart.descuento,
      tipo_comprobante: extras.tipo_comprobante,
      items: cart.items.map((x) => ({
        producto_id: x.producto_id,
        cantidad: x.cantidad,
        precio_unit: x.precio_unit,
      })),
      pagos,
    };
    try {
      const { data } = await api.post<{
        id: number;
        fecha: string;
        total: number;
      }>("/api/sales", payload);
      const nroVenta = data.id;

      let mensaje = `Venta #${nroVenta} cerrada`;
      const compLabel =
        extras.tipo_comprobante === "factura_b"
          ? "Factura B"
          : extras.tipo_comprobante === "whatsapp"
            ? "WhatsApp"
            : "ticket";

      if (extras.imprimir) {
        const printRes = await printTicket({
          numero: nroVenta,
          fecha: data.fecha,
          cajero: user?.nombre,
          cliente: cart.cliente?.nombre ?? null,
          items: cart.items.map((x) => ({
            nombre: x.nombre,
            cantidad: x.cantidad,
            precio_unit: x.precio_unit,
            subtotal: x.cantidad * x.precio_unit,
          })),
          total: data.total,
          pagos,
          vuelto: Math.max(0, pagado - total),
        });
        mensaje = printRes.ok
          ? `Venta #${nroVenta} — ${compLabel} impreso`
          : `Venta #${nroVenta} cerrada — sin impresora (${printRes.error})`;
        if (!printRes.ok) console.warn("No se pudo imprimir:", printRes.error);
      } else if (extras.tipo_comprobante === "whatsapp") {
        // TODO: endpoint backend para enviar comprobante por WhatsApp al cliente
        mensaje = `Venta #${nroVenta} cerrada — envío por WhatsApp pendiente`;
      } else {
        mensaje = `Venta #${nroVenta} cerrada — sin impresión`;
      }

      setToast(mensaje);
      cart.clear();
      setShowPago(false);
    } catch (e) {
      console.error("Error cerrando venta:", e);
      alert("Error al cerrar la venta. Quedará en cola offline.");
    }
  };

  return (
    <div className="flex h-full overflow-hidden">
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-[opacity,filter] duration-200 ${
          showPago ? "opacity-40 pointer-events-none saturate-50" : ""
        }`}
        aria-hidden={showPago}
      >
        <BrandStrip />

        <div className="px-5 pt-4 pb-3 flex items-center gap-2.5 flex-wrap">
          {CATEGORIAS.map((c) => {
            const active = categoria === c;
            return (
              <button
                key={c}
                onClick={() => setCategoria(c)}
                className={`relative px-4 py-2 rounded-full font-semibold text-sm transition-colors ${
                  active
                    ? "bg-[var(--ink)] text-white"
                    : "bg-white text-[var(--ink-2)] hover:bg-slate-50 border border-[var(--hairline)]"
                }`}
              >
                {c}
                {active && (
                  <span
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                    style={{ background: "var(--brand-red)" }}
                  />
                )}
              </button>
            );
          })}
          <div className="flex-1" />
          <input
            ref={localFilterRef}
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar grilla (F3)"
            className="px-3 py-2 bg-white border border-[var(--hairline)] rounded-full text-sm w-52 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="text-center text-slate-400 py-12 text-sm">
              Cargando productos...
            </div>
          ) : visibles.length === 0 ? (
            <div className="text-center text-slate-400 py-12 text-sm">
              Sin productos para mostrar
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-3">
              {visibles.map((p) => {
                const emoji = emojiFor(p);
                const pal = paletteFor(p);
                return (
                <button
                  key={p.id}
                  onClick={() => addProducto(p)}
                  className="product-card bg-white border border-[var(--hairline)] rounded-2xl p-3 text-left hover:border-[var(--mint)] hover:shadow-md"
                >
                  <div
                    className="aspect-square rounded-2xl mb-2.5 flex items-center justify-center relative"
                    style={{ background: tileBackground(pal) }}
                  >
                    {emoji ? (
                      <span className="text-4xl">{emoji}</span>
                    ) : (
                      <span
                        className="text-3xl font-bold tracking-tight"
                        style={{ color: pal.ink }}
                      >
                        {initialsFor(p.nombre)}
                      </span>
                    )}
                    {typeof p.stock === "number" && p.stock >= 0 && (
                      <span className="absolute top-1.5 right-1.5 bg-white/85 text-[var(--ink-muted)] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        {p.stock}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-[13px] text-[var(--ink)] leading-tight line-clamp-2 min-h-[34px]">
                    {toTitleCase(p.nombre)}
                  </div>
                  <div className="text-base font-extrabold text-[var(--ink)] mt-0.5 tabular-nums">
                    {fmt(p.precio)}
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showPago ? (
        <PagoPanel
          total={cart.total()}
          clienteLabel={cart.cliente?.nombre ?? "Consumidor Final"}
          itemsCount={cart.items.length}
          onClose={() => setShowPago(false)}
          onConfirm={(pagos, extras) => cerrarVenta(pagos, extras)}
        />
      ) : (
      <aside className="w-[360px] flex-shrink-0 bg-white border border-[var(--hairline)] shadow-[var(--shadow-md)] flex flex-col rounded-3xl m-3.5 ml-0">
        <div className="p-4 border-b border-[var(--hairline)] flex items-center gap-3">
          <div className="w-7 h-7 flex-shrink-0">
            <img
              src="/logo.png"
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-[var(--ink-faint)] tracking-wider uppercase">
              Cliente
            </div>
            <button
              onClick={() => setShowCliente(true)}
              className="text-base font-bold text-[var(--ink)] flex items-center gap-1 mt-0.5 truncate"
            >
              <span className="truncate">
                {cart.cliente?.nombre ?? "Consumidor Final"}
              </span>
              <kbd className="ml-1 px-1.5 py-0.5 bg-[var(--bg-sunken)] rounded text-[10px] text-[var(--ink-muted)] font-mono font-semibold">
                F2
              </kbd>
            </button>
          </div>
          <button
            onClick={() => cart.clear()}
            className="text-[11px] text-[var(--ink-faint)] hover:text-[var(--red)] font-semibold"
          >
            Vaciar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {cart.items.length === 0 && <EmptyCartPanel />}
          {cart.items.map((it) => {
            const fake: Producto = {
              id: it.producto_id,
              sku: it.sku,
              barcode: null,
              nombre: it.nombre,
              precio: it.precio_unit,
            };
            const pal = paletteFor(fake);
            const emoji = emojiFor(fake);
            return (
              <div
                key={it.producto_id}
                className="flex items-center gap-3 px-1 py-3 border-b border-[var(--hairline)]"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: pal.bg, color: pal.ink }}
                >
                  {emoji ? (
                    <span className="text-2xl">{emoji}</span>
                  ) : (
                    <span className="text-[13px] font-bold">
                      {initialsFor(fake.nombre)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="font-semibold text-[13px] text-[var(--ink)] truncate flex-1 uppercase tracking-wide leading-tight">
                      {it.nombre}
                      {it.pesable && (
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold uppercase tracking-wide">
                          kg
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => cart.removeItem(it.producto_id)}
                      className="text-[var(--ink-faint)] hover:text-[var(--red)] transition-colors"
                      title="Quitar"
                      aria-label="Quitar del carrito"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => {
                        const step = it.pesable ? 0.1 : 1;
                        cart.setQty(
                          it.producto_id,
                          Math.max(
                            it.pesable ? 0.001 : 1,
                            +(it.cantidad - step).toFixed(3)
                          )
                        );
                      }}
                      className="w-7 h-7 rounded-lg bg-[var(--bg-sunken)] hover:bg-slate-200 text-[var(--ink-2)] text-sm font-bold flex items-center justify-center"
                    >
                      −
                    </button>
                    {it.pesable ? (
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={it.cantidad}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (Number.isFinite(v) && v > 0)
                            cart.setQty(it.producto_id, v);
                        }}
                        className="w-16 text-center text-sm font-bold border border-[var(--hairline)] rounded-lg px-1 py-0.5 tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--mint)]"
                      />
                    ) : (
                      <span className="text-sm font-bold w-5 text-center tabular-nums">
                        {it.cantidad}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        const step = it.pesable ? 0.1 : 1;
                        cart.setQty(
                          it.producto_id,
                          +(it.cantidad + step).toFixed(3)
                        );
                      }}
                      className="w-7 h-7 rounded-lg bg-[var(--bg-sunken)] hover:bg-slate-200 text-[var(--ink-2)] text-sm font-bold flex items-center justify-center"
                    >
                      +
                    </button>
                    <span className="text-[11px] text-[var(--ink-faint)] ml-auto">
                      {fmt(it.precio_unit)} {it.pesable ? "/kg" : "c/u"}
                    </span>
                  </div>
                </div>
                <div className="font-extrabold text-[var(--ink)] whitespace-nowrap tabular-nums text-base">
                  {fmt(it.cantidad * it.precio_unit)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[var(--hairline)] px-5 py-4">
          <div className="flex justify-between text-[13px] text-[var(--ink-muted)] mb-1.5">
            <span>Subtotal</span>
            <span className="font-semibold text-[var(--ink)]">
              {fmt(cart.subtotal())}
            </span>
          </div>
          <div className="flex justify-between text-[13px] text-[var(--ink-muted)] items-center mb-3">
            <span>Descuento</span>
            <input
              type="number"
              min={0}
              value={cart.descuento}
              onChange={(e) => cart.setDescuento(Number(e.target.value) || 0)}
              className="w-24 bg-[var(--bg-sunken)] rounded-lg px-2 py-1 text-right text-sm font-bold text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--mint)]"
            />
          </div>
          <div className="flex justify-between items-baseline mb-3.5">
            <span className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">
              Total
            </span>
            <span className="text-4xl font-extrabold text-[var(--ink)] tracking-tight tabular-nums">
              {fmt(cart.total())}
            </span>
          </div>

          <div className="flex gap-2.5">
            <button
              disabled={cart.items.length === 0}
              className="px-4 py-3.5 bg-[var(--bg-sunken)] hover:bg-slate-200 text-[var(--ink)] font-bold rounded-2xl text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Presupuesto
            </button>
            <button
              disabled={cart.items.length === 0}
              onClick={() => setShowPago(true)}
              className="flex-1 py-3.5 bg-[var(--mint)] hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold rounded-2xl text-base flex items-center justify-center gap-2.5 shadow-[0_10px_24px_-10px_rgba(31,200,155,0.65)]"
            >
              COBRAR
              <kbd className="px-1.5 py-0.5 bg-white/22 rounded text-xs font-mono font-bold">
                F4
              </kbd>
            </button>
          </div>
        </div>
      </aside>
      )}

      {pesableModal && (
        <PesableModal
          producto={pesableModal}
          onClose={() => setPesableModal(null)}
          onConfirm={(cantidad) => addPesable(pesableModal, cantidad)}
        />
      )}

      {showCliente && (
        <ClienteModal
          onClose={() => setShowCliente(false)}
          onPick={(c) => {
            cart.setCliente(c);
            setShowCliente(false);
          }}
          onConsumidorFinal={() => {
            cart.setCliente(null);
            setShowCliente(false);
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-30 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-pulse">
          {toast}
        </div>
      )}
    </div>
  );
}

function EmptyCartPanel() {
  const { data: resumen } = useQuery({
    queryKey: ["reporte-diario"],
    queryFn: async () => {
      const hoy = new Date().toISOString().slice(0, 10);
      const { data } = await api.get<{
        cantidad_ventas: number;
        total: number;
      }>(`/api/reports/ventas/diario`, { params: { dia: hoy } });
      return data;
    },
    refetchInterval: 30_000,
    retry: false,
  });

  return (
    <div className="space-y-4 py-2">
      <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200/60 p-4">
        <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
          Ventas de hoy
        </div>
        <div className="text-3xl font-bold text-slate-900 mt-1">
          {fmt(resumen?.total ?? 0)}
        </div>
        <div className="text-xs text-slate-600 mt-1">
          {resumen?.cantidad_ventas ?? 0} tickets cerrados
        </div>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-200/60 p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">
          Atajos
        </div>
        <div className="space-y-2 text-sm text-slate-700">
          <div className="flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs text-slate-600 font-mono w-8 text-center">
              F1
            </kbd>
            <span>Buscar producto</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs text-slate-600 font-mono w-8 text-center">
              F2
            </kbd>
            <span>Elegir cliente</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs text-slate-600 font-mono w-8 text-center">
              F4
            </kbd>
            <span>Cobrar</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs text-slate-600 font-mono w-8 text-center">
              Esc
            </kbd>
            <span>Cancelar</span>
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-slate-400 pt-2">
        Carrito vacío — agregá productos desde la grilla
      </div>
    </div>
  );
}

function PesableModal({
  producto,
  onClose,
  onConfirm,
}: {
  producto: Producto;
  onClose: () => void;
  onConfirm: (cantidad: number) => void;
}) {
  const [valor, setValor] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const cantidad = parseFloat(valor);
  const valido = Number.isFinite(cantidad) && cantidad > 0;
  const subtotal = valido ? cantidad * producto.precio : 0;

  const confirmar = () => {
    if (valido) onConfirm(cantidad);
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-30 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-lg font-bold text-slate-900">{producto.nombre}</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            ${producto.precio.toLocaleString("es-AR")} por kg
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1 block">
            Cantidad en kg
          </label>
          <input
            ref={inputRef}
            type="number"
            step="0.001"
            min="0.001"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmar();
              if (e.key === "Escape") onClose();
            }}
            placeholder="0.250"
            className="w-full px-4 py-3 text-2xl font-bold text-center border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <div className="flex gap-2 mt-2 text-xs">
            {[0.1, 0.25, 0.5, 1].map((q) => (
              <button
                key={q}
                onClick={() => setValor(String(q))}
                className="flex-1 py-1.5 bg-slate-100 hover:bg-slate-200 rounded font-medium"
              >
                {q} kg
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-baseline justify-between pt-2 border-t border-slate-100">
          <span className="text-sm text-slate-500 uppercase tracking-wide">Subtotal</span>
          <span className="text-2xl font-bold text-slate-900">
            ${subtotal.toLocaleString("es-AR", { maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            onClick={onClose}
            className="py-3 bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-medium rounded-xl"
          >
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={!valido}
            className="py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-bold rounded-xl"
          >
            Agregar
          </button>
        </div>
      </div>
    </div>
  );
}


function ClienteModal({
  onClose,
  onPick,
  onConsumidorFinal,
}: {
  onClose: () => void;
  onPick: (c: { id: number; nombre: string }) => void;
  onConsumidorFinal: () => void;
}) {
  const [q, setQ] = useState("");
  const [list, setList] = useState<{ id: number; nombre: string }[]>([]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.length < 2) {
        setList([]);
        return;
      }
      try {
        const { data } = await api.get<{ id: number; nombre: string }[]>(
          "/api/customers",
          { params: { q } }
        );
        setList(data);
      } catch {
        setList([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-bold text-slate-900">Seleccionar cliente</h3>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nombre o CUIT"
          className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white"
        />
        <ul className="max-h-64 overflow-auto divide-y divide-slate-100">
          {list.map((c) => (
            <li
              key={c.id}
              onClick={() => onPick(c)}
              className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm"
            >
              {c.nombre}
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <button
            onClick={onConsumidorFinal}
            className="flex-1 py-2.5 bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-medium rounded-xl text-sm"
          >
            Consumidor Final
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
